// Navegación inferior única para móvil, compartida por Pulso y Reconocimiento. Reemplaza dos
// patrones que hacían lo mismo con nombres distintos: `.mobile-bottom-nav` (Pulso, antes
// `MobileBottomNav` en PulsoLayout.tsx) y `.frontline-tabbar` (Reconocimiento, antes
// `FrontlineTabBar` en accessShared.tsx). Misma fuente de entradas que ya usa el Sidebar de
// escritorio (useVisibleNavLinks + RECOGNITION_NAV_LINKS/canAccessRecognitionEntry): un usuario ve
// en móvil exactamente lo mismo que vería expandiendo el riel en escritorio.
//
// El componente se dispara en dos "modos" según la zona (Pulso vs. `/app/...`), cada uno resuelto
// por su propio componente de módulo — nunca definidos dentro del cuerpo de `MobileNav` (regla del
// proyecto: no anidar definiciones de componentes).
//
// Ver docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 4.

import { useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { hasFrontlineSession, clearFrontlineSession } from '@/auth/frontlineSession';
import { resetTokenCache } from '@/auth/tokenCache';
import { canAccessRecognitionEntry, isRecognitionAdmin, useUserContext, VIEW_ACCESS } from '@/contexts/UserContext';
import { useEffectiveRoles } from '@/auth/RoleGuard';
import { useAdminPanelContext } from '@/contexts/AdminPanelContext';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { isAdminPath, RECOGNITION_ADMIN_HOME, VIEW_PATHS } from '@/router/pulsoRoutes';
import {
  EXCLUSIVE_RECOGNITION_NAV_IDS, RECOGNITION_NAV_LINKS, recognitionIconClass,
} from '@/views/recognition/recognitionNavLinks';
import { useRecognitionEntryGate } from '@/views/recognition/useCampaignNavEntry';
import { NotificationBell } from '@/views/recognition/components';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import { AdminFlyout } from '@/components/layout/Sidebar/AdminFlyout';
import { LogoutButton } from '@/components/common/LogoutButton';
import { pickTitle, useDocumentationPills, usePillActivation, PillActivationModal } from '@/components/layout/PillDocuments';
import './MobileNav.css';
import { BASE_PATH, withBase } from '@/utils/basePath';

/**
 * Nav inferior móvil de Pulso — solo entradas accesibles para el rol actual.
 *
 * El link "Administración" replica el comportamiento del botón de escritorio (Sidebar.tsx): en
 * vez de solo navegar, también abre el flyout de navegación del admin (AdminFlyout) — en
 * `≤768px` el `.side-nav` completo (incluido el botón de escritorio que hoy controla ese flyout)
 * se oculta por CSS, así que sin este caso especial no había ninguna forma de abrir el flyout en
 * móvil (hueco encontrado en verificación manual de Task 12).
 *
 * El propio `<AdminFlyout />` se renderiza como hermano del `<nav>`, no dentro: en escritorio
 * vive anclado a `.admin-link-wrap` (posición absoluta relativa a ese contenedor), pero ese
 * contenedor también está oculto en móvil. La regla `@media (max-width: 768px)` de `.admin-flyout`
 * (Sidebar.css) ya lo convierte en overlay `position: fixed; inset: 0`, así que no depende de qué
 * padre lo contenga — y `.mobile-admin-flyout-wrap` (misma hoja) mantiene esta segunda instancia
 * fuera de pantalla en escritorio para que nunca coincidan las dos a la vez.
 */
function PulsoMobileNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { links } = useVisibleNavLinks();
  const { isAdminFlyoutOpen, setIsAdminFlyoutOpen } = useAdminPanelContext();
  const isOnAdmin = isAdminPath(location.pathname);

  return (
    <>
      <nav className="mobile-bottom-nav" aria-label={t('nav.mobileNav')}>
        {links.map(({ view, icon, mobileIcon, labelKey }) => {
          if (view === 'admin') {
            return (
              <button
                key={view}
                type="button"
                // `mbn-admin-link`: clase estable (no depende de `isOnAdmin`) que el `mousedown`
                // de clic-fuera de Sidebar.tsx reconoce como "dentro" — igual que `.admin-flyout`
                // para el contenido del flyout. Sin ella, un segundo clic sobre ESTE botón (para
                // cerrar el flyout, F-6) quedaba atrapado en la misma carrera que F-1/F-2: el
                // `mousedown` lo cerraba primero, y el `onClick` del propio botón —con
                // `isAdminFlyoutOpen` de su closure aún en `true`— lo volvía a abrir, y el segundo
                // clic terminaba sin efecto neto (detectado con un test `user-event` real, no con
                // `HTMLElement.click()`).
                className={`mbn-link mbn-admin-link${isOnAdmin ? ' active' : ''}`}
                onClick={() => {
                  if (!isOnAdmin) navigate(VIEW_PATHS.admin);
                  // Alterna, igual que el botón de escritorio (Sidebar.tsx) — con `aria-expanded`
                  // anunciando el estado real, un `setIsAdminFlyoutOpen(true)` fijo dejaba sin
                  // forma de cerrar el flyout tocando este mismo botón de nuevo (F-6).
                  setIsAdminFlyoutOpen(!isAdminFlyoutOpen);
                }}
                aria-label={t(labelKey)}
                aria-haspopup="true"
                aria-expanded={isAdminFlyoutOpen}
                aria-current={isOnAdmin ? 'page' : undefined}
              >
                <i className={`fas ${mobileIcon ?? icon}`} aria-hidden="true" />
                <span>{t(labelKey)}</span>
              </button>
            );
          }

          return (
            <NavLink
              key={view}
              to={VIEW_PATHS[view]}
              end={VIEW_PATHS[view] === '/'}
              className={({ isActive }) => `mbn-link${isActive ? ' active' : ''}`}
              aria-label={t(labelKey)}
            >
              <i className={`fas ${mobileIcon ?? icon}`} aria-hidden="true" />
              <span>{t(labelKey)}</span>
            </NavLink>
          );
        })}
        <LogoutButton variant="mobile" />
      </nav>

      {isAdminFlyoutOpen && (
        <div className="mobile-admin-flyout-wrap">
          {/* Overlay a pantalla completa en móvil: se cierra solo al elegir una sección (M-E,
              segunda revisión final) — a diferencia del popover de escritorio (Sidebar.tsx), aquí
              cubre la pantalla entera si queda abierto, y su z-index intercepta el toque sobre este
              mismo botón "Administración" para un segundo cierre manual. */}
          <AdminFlyout shouldCloseOnSelect />
        </div>
      )}
    </>
  );
}

/**
 * Bottom sheet de "Más" (idioma/documentos) — portal a `document.body` (BUG 2026-09-04). Estos
 * paneles vivían anidados dentro de `.frontline-mas-overlay`, que es `position: fixed; z-index: 60`
 * A PROPÓSITO (deja libre la franja del nav — `z-index: 200` — para poder cerrar "Más" tocando ese
 * mismo botón de nuevo, ver el comentario de esa regla en recognition-shell.css). Un
 * `z-index` alto puesto en un DESCENDIENTE de ese overlay nunca puede ganarle al nav: al ser
 * `.frontline-mas-overlay` su propia stacking context, todo lo que va adentro queda acotado al
 * rango local del ancestro (60) frente a cualquier hermano de FUERA de esa stacking context —
 * el `z-index: 2000` de `.modal-overlay` no importa, compite en el nivel equivocado. Por eso el
 * selector de idioma (lista larga) casi no se notaba recortado por el nav y el de documentos (lista
 * corta o vacía) quedaba directamente detrás. Mismo patrón que ya usa `Modal`
 * (components/common/Modal) para el mismo tipo de problema — ahí por `transform` de un ancestro, acá
 * por `z-index`, misma causa raíz: un ancestro posicionado acota a sus hijos.
 */
function FrontlineSheet({ onClose, children }: Readonly<{ onClose: () => void; children: ReactNode }>) {
  return createPortal(
    <div className="modal-overlay modal-overlay--bottom modal-overlay--flush" onClick={onClose}>
      {children}
    </div>,
    document.body,
  );
}

/**
 * Nav inferior móvil de Reconocimiento — reemplaza a `FrontlineTabBar` (accessShared.tsx).
 *
 * Entradas de `RECOGNITION_NAV_LINKS` (misma fuente que el submenú "Reconocimientos" del Sidebar),
 * más el mismo chequeo "administra el Programa de Reconocimiento" que ya usaba `FrontlineTabBar`
 * (`isAdmin`: unión de `VIEW_ACCESS.admin` — que desde la Fase 4 ya incluye a los tres roles de
 * Brillamos — y `isRecognitionAdmin`).
 *
 * "Administración" (2026-09-05, ver AppLayout.tsx) — botón-toggle, igual que en `PulsoMobileNav`:
 * `/app/admin` se agregó a `FRONTLINE_ROUTE_PREFIXES`, así que `<MobileNav />` YA NO se desmonta al
 * navegar ahí (motivo por el que antes era un `NavLink` normal — ver el historial de este archivo
 * para esa versión anterior, ya no vigente). El toggle reusa el mismo `AdminPanelContext` que abre
 * `<MobileAdminFlyoutPortal/>` (AppLayout.tsx): con `NavLink`, una vez en `/app/admin` con el
 * flyout ya cerrado (p. ej. tras elegir una sección), volver a tocar el link no lo reabría — el
 * efecto de deep-link de Sidebar.tsx (`isAdminPath` → `setIsAdminFlyoutOpen(true)`) solo reacciona
 * a un CAMBIO de `pathname`, y `NavLink` a la misma ruta no produce ninguno.
 *
 * "Más" agrupa Documentos e Idioma — port literal del overlay de `FrontlineTabBar` (paridad demo
 * F8, H-96/H-97), reutilizando `.frontline-mas-*`/`.frontline-lang-*` (recognition-shell.css) tal
 * cual. "Mi perfil" salió del menú: ahora es una entrada normal del nav (vía `RECOGNITION_NAV_LINKS`).
 *
 * "Documentos" (BUG 2026-09-04) — abría un único PDF hardcodeado (`LEGAL_RULES_URL`) sin enterarse
 * de que un admin puede configurar varias pills (documento/url/video, ver `HomeRightRail.tsx`, el
 * equivalente de escritorio). Esas pills eran, en la práctica, inalcanzables desde un celular: no
 * hay riel lateral en frontline móvil (`.plataforma-shell--frontline .side-nav { display: none }`)
 * y este botón no consultaba `usePillsForMe`. Ahora "Documentos" abre una lista con las mismas
 * pills reales (`useDocumentationPills`, compartido con el rail de escritorio vía
 * `PillDocuments.tsx`) y activa la que se toque con la misma lógica de visor
 * (`usePillActivation`/`PillActivationModal`) — un admin nunca vuelve a configurar contenido que
 * solo exista para quien entra desde una pantalla grande.
 *
 * "Cerrar sesión" NO reutiliza el `<LogoutButton />` genérico (fix round posterior a Task 14,
 * hallazgo real de revisión): ese componente devuelve `null` dentro de Teams ("la sesión la
 * gestiona el SSO de Teams" — cierto para el Sidebar de escritorio, que SIEMPRE tiene la barra
 * lateral visible en Teams), pero en Teams frontline móvil el `.side-nav` completo está oculto por
 * CSS, así que reusarlo hacía desaparecer la ÚNICA forma de cerrar sesión que existía en esta zona
 * (el `FrontlineTabBar` original SÍ tenía una, sin distinguir plataforma). Se porta esa misma
 * lógica original tal cual (`handleLogout` de abajo): con cuenta MSAL activa, `logoutRedirect`; sin
 * ella (incluido dentro de Teams), navega a `/login` — nunca desaparece.
 */
function RecognitionMobileNav() {
  const { t } = useTranslation();
  const { t: tReco, i18n } = useTranslation('recognition');
  const { activeRole } = useVisibleNavLinks();
  const { demoUser } = useUserContext();
  const roles = useEffectiveRoles();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdminFlyoutOpen, setIsAdminFlyoutOpen } = useAdminPanelContext();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isLangPickerOpen, setIsLangPickerOpen] = useState(false);
  const [isDocsPickerOpen, setIsDocsPickerOpen] = useState(false);

  // Reconocer, Brillamos y la campaña personalizada son mutuamente excluyentes — mismo criterio
  // que el Sidebar de escritorio (ver comentario ahí): nunca las tres a la vez.
  // Mientras la campaña activa está en vuelo NO se muestra ninguna entrada excluyente (ver Sidebar):
  // evita el salto de "Reconocer" a "Brillamos" al resolver la consulta.
  const { entryId: activeRecognitionEntryId, isLoading: recognitionEntryLoading } = useRecognitionEntryGate(demoUser.ou);
  const recognitionLinks = RECOGNITION_NAV_LINKS.filter(({ id: linkId }) => canAccessRecognitionEntry(linkId, activeRole)
    && (!EXCLUSIVE_RECOGNITION_NAV_IDS.includes(linkId)
      || (!recognitionEntryLoading && linkId === activeRecognitionEntryId)));
  const isAdmin = roles.some((role) => VIEW_ACCESS.admin.includes(role)) || isRecognitionAdmin(roles);
  const isOnAdmin = isAdminPath(location.pathname);
  const currentLang = i18n.language?.split('-')[0] ?? 'es';

  // Mismos datos y misma lógica de activación que el rail de escritorio (HomeRightRail.tsx) —
  // ver el porqué en PillDocuments.tsx.
  const { isFetched: pillsFetched, pills: docPills, docUrlById } = useDocumentationPills(currentLang);
  const pillViewer = usePillActivation(docUrlById);

  const closeMore = useCallback(() => {
    setIsMoreOpen(false);
    setIsLangPickerOpen(false);
    setIsDocsPickerOpen(false);
  }, []);

  // Prioridad a la sesión Frontline (JWT propio en localStorage): se limpia y se recarga a /login con
  // una navegación de documento COMPLETA (`window.location.assign`, no `navigate` de react-router) para
  // resetear en duro el QueryClient y los contextos — así no se arrastran datos cacheados del usuario
  // anterior en dispositivos compartidos (kiosco Frontline). Si hay cuenta MSAL, logout OIDC de Azure AD
  // (postLogoutRedirectUri en el default = origin, la única Redirect URI registrada). Sin ninguna, a
  // /login como fallback. No gatea por plataforma a propósito, ver comentario del componente arriba.
  const handleLogout = useCallback(() => {
    // Ver LogoutButton: la cache de token esta en memoria del modulo y solo se reinicia con una
    // navegacion de documento completa. Si `logoutRedirect` rechaza no la hay, asi que se limpia aqui.
    resetTokenCache();

    if (hasFrontlineSession()) {
      clearFrontlineSession();
      window.location.assign(withBase('/login'));
      return;
    }
    if (accounts.length > 0) {
      // `postLogoutRedirectUri` explícito, igual que en LogoutButton: sin él MSAL usa el
      // `redirectUri` de la configuración y, en el ambiente de desarrollo, el cierre de sesión
      // dejaba al usuario en el portal de QA. En QA y PROD `BASE_URL` es '/', así que el valor
      // resultante es el mismo de siempre (`https://host/`).
      instance
        .logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: `${window.location.origin}${BASE_PATH}`,
        })
        .catch((error: unknown) => {
        if (import.meta.env.DEV) {
          console.error('MobileNav: no se pudo cerrar sesión en Reconocimiento', error);
        }
      });
    } else {
      window.location.assign(withBase('/login'));
    }
  }, [instance, accounts]);

  return (
    <>
      {/* `--compact`: la zona Reconocimiento mete hasta 5 enlaces + Admin + campana + "Más" +
          logout en el mismo ancho. `MobileNav.css` esconde las etiquetas por debajo de 420 px
          (queda el icono, con `aria-label` como nombre accesible) para que los objetivos no bajen
          de ~50 px. La zona Pulso no lleva el modificador (menos entradas, fuera de alcance). */}
      <nav className="mobile-bottom-nav mobile-bottom-nav--compact" aria-label={t('nav.mobileNav')}>
        {recognitionLinks.map((link) => (
          <NavLink
            key={link.id}
            to={link.to}
            className={({ isActive }) => `mbn-link${isActive ? ' active' : ''}`}
            aria-label={tReco(link.labelKey)}
            onClick={closeMore}
          >
            {link.emoji
              ? <span aria-hidden="true">{link.emoji}</span>
              : <i className={recognitionIconClass(link)} aria-hidden="true" />}
            <span>{tReco(link.labelKey)}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <button
            type="button"
            className={`mbn-link mbn-admin-link${isOnAdmin ? ' active' : ''}`}
            onClick={() => {
              closeMore();
              if (!isOnAdmin) navigate(RECOGNITION_ADMIN_HOME);
              setIsAdminFlyoutOpen(!isAdminFlyoutOpen);
            }}
            aria-label={t('nav.admin')}
            aria-haspopup="true"
            aria-expanded={isAdminFlyoutOpen}
            aria-current={isOnAdmin ? 'page' : undefined}
          >
            <i className="fas fa-cog" aria-hidden="true" />
            <span>{t('nav.admin')}</span>
          </button>
        )}

        {/* RF-164 — La campana vive en el riel lateral, que está OCULTO por debajo de 768 px. Sin
            montarla también aquí, en móvil no había forma de ver ni de limpiar los avisos mientras el
            perfil seguía enseñando el contador de no leídas: un número que el usuario no podía bajar.
            El propio componente se dibuja como pestaña con `variant="mobile"`. */}
        <NotificationBell variant="mobile" />

        <button
          type="button"
          className={`mbn-link${isMoreOpen ? ' active' : ''}`}
          onClick={() => setIsMoreOpen((prev) => !prev)}
          aria-expanded={isMoreOpen}
          aria-label={tReco('frontlineMore.tab')}
        >
          <i className="fas fa-ellipsis-h" aria-hidden="true" />
          <span>{tReco('frontlineMore.tab')}</span>
        </button>
        {/* "Cerrar sesión" se movió al overlay "Más" (abajo) para descomprimir la barra (P1-22,
            auditoría responsive 2026-09-01). Sigue SIEMPRE accesible: "Más" es una entrada fija de
            la barra y en Teams frontline móvil, con el riel lateral oculto, esta ruta era la única
            forma de cerrar sesión — sigue estándolo, a un toque de distancia. */}
      </nav>

      {isMoreOpen && (
        <div className="frontline-mas-overlay">
          <div className="frontline-mas-menu">
            <h2 className="frontline-mas-title">{tReco('frontlineMore.title')}</h2>
            <div className="frontline-mas-list">
              <button
                type="button"
                className="frontline-mas-item"
                onClick={() => setIsDocsPickerOpen(true)}
              >
                <i className="fas fa-file-pdf" aria-hidden="true" />
                <span>{tReco('frontlineMore.documents')}</span>
                <i className="fas fa-chevron-right frontline-mas-arrow" aria-hidden="true" />
              </button>
              <button type="button" className="frontline-mas-item" onClick={() => setIsLangPickerOpen(true)}>
                <i className="fas fa-globe" aria-hidden="true" />
                <span>{tReco('frontlineMore.language')} ({currentLang.toUpperCase()})</span>
                <i className="fas fa-chevron-right frontline-mas-arrow" aria-hidden="true" />
              </button>
              <button type="button" className="frontline-mas-item frontline-mas-item--logout" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt" aria-hidden="true" />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          </div>

          {isLangPickerOpen && (
            <FrontlineSheet onClose={() => setIsLangPickerOpen(false)}>
              <div className="frontline-lang-modal" onClick={(event) => event.stopPropagation()}>
                <h3 className="frontline-lang-modal-title">{tReco('frontlineMore.selectLanguage')}</h3>
                <div className="frontline-lang-options">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`frontline-lang-option${currentLang === lang.code ? ' frontline-lang-option--active' : ''}`}
                      onClick={() => {
                        i18n.changeLanguage(lang.code).catch((error: unknown) => {
                          if (import.meta.env.DEV) {
                            console.error('MobileNav: no se pudo cambiar de idioma', error);
                          }
                        });
                        setIsLangPickerOpen(false);
                      }}
                    >
                      <span className="frontline-lang-flag" aria-hidden="true">{lang.flag}</span>
                      <span>{lang.native}</span>
                      {currentLang === lang.code && <i className="fas fa-check frontline-lang-check" aria-hidden="true" />}
                    </button>
                  ))}
                </div>
              </div>
            </FrontlineSheet>
          )}

          {isDocsPickerOpen && (
            <FrontlineSheet onClose={() => setIsDocsPickerOpen(false)}>
              <div className="frontline-lang-modal" onClick={(event) => event.stopPropagation()}>
                <h3 className="frontline-lang-modal-title">{tReco('frontlineMore.documents')}</h3>
                {!pillsFetched ? (
                  <p className="frontline-docs-status">{tReco('common.loading')}</p>
                ) : docPills.length === 0 ? (
                  <p className="frontline-docs-status">{tReco('common.empty')}</p>
                ) : (
                  <div className="frontline-lang-options">
                    {docPills.map((pill) => (
                      <button
                        key={pill.id}
                        type="button"
                        className="frontline-lang-option"
                        onClick={() => {
                          pillViewer.activate(pill);
                          closeMore();
                        }}
                      >
                        <span className="frontline-docs-icon" style={{ background: pill.color }}>
                          <i className={`fas ${pill.icono}`} aria-hidden="true" />
                        </span>
                        <span>{pickTitle(pill.titulo, currentLang)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FrontlineSheet>
          )}
        </div>
      )}

      {/* Fuera del bloque `isMoreOpen`: al tocar una pill, `closeMore()` cierra "Más" pero el
          visor (documento/video) debe seguir visible. */}
      <PillActivationModal viewer={pillViewer} lang={currentLang} />
    </>
  );
}

/**
 * Navegación inferior única para móvil. Elige la zona por el `pathname` actual — `/app/...` es
 * Reconocimiento, cualquier otra ruta es Pulso — mismo criterio de "una sola lista visible a la
 * vez" que ya usa el Sidebar de escritorio con sus dos secciones, adaptado a que en móvil no hay
 * espacio para mostrar ambas.
 */
export function MobileNav() {
  const location = useLocation();
  const isRecognitionRoute = location.pathname.startsWith('/app/');
  return isRecognitionRoute ? <RecognitionMobileNav /> : <PulsoMobileNav />;
}

export default MobileNav;
